import React from 'react';

import { Box, radioClasses, Typography } from '@mui/material';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { EMPLOYERS_DEFAULT_PIN_SETTINGS_FORM_VALUES } from '@clearverify/constants';

import { SignUpMethod } from 'src/api';
import { Button } from 'src/components/common';
import { Field, Form, RadioGroupField } from 'src/components/finalForm';
import { SIGN_UP_METHOD_OPTIONS } from 'src/constants/employer';
import { useAuthSignup } from 'src/hooks/auth';
import type { SxProp } from 'src/types/mui';

import { EmployeeSignupMethodFormFields, EmployeeSignupMethodFormValues } from '../types';
import { employeeSignupMethodFormValidationSchema, prepareSignUpMethod } from '../utils';

export const EmployeeSignupMethodForm: React.FC = () => {
  const { inviteData, updateFormData, goToNextStep } = useAuthSignup();

  const initialValues: EmployeeSignupMethodFormValues = React.useMemo(
    () => ({
      [EmployeeSignupMethodFormFields.signUpMethod]:
        inviteData?.isPeoUnderPeoPayrollProvider && inviteData.predefinedSignUpMethod
          ? prepareSignUpMethod(inviteData.predefinedSignUpMethod)
          : SignUpMethod.Ssn,
    }),
    [inviteData?.isPeoUnderPeoPayrollProvider, inviteData?.predefinedSignUpMethod],
  );

  const handleSubmit = React.useCallback(
    (formValues: EmployeeSignupMethodFormValues) => {
      updateFormData({
        ...formValues,
        ...EMPLOYERS_DEFAULT_PIN_SETTINGS_FORM_VALUES,
      });
      goToNextStep();
    },
    [goToNextStep, updateFormData],
  );

  return (
    <Box>
      <Form<EmployeeSignupMethodFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        subscription={{ valid: true, values: true, submitting: true, errors: true }}
        containerProps={{ mt: 2 }}
        validate={validateWithYupSchema(employeeSignupMethodFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <Box sx={boxStyles} rowGap={5}>
              <Box>
                <Typography variant="h5">Employee Sign Up PIN</Typography>
                <Typography variant="body1" color="text.secondary" mt={1}>
                  Employees will use this 8 digit PIN as the first part of the sign-up process to
                  confirm identity. This is an optional additional layer of security.
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Format:
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  XXXX-YYYY where XXXX - 4 last digits of SSN, YYYY - year of birth
                </Typography>

                <Field
                  fullWidth
                  name={EmployeeSignupMethodFormFields.signUpMethod}
                  component={RadioGroupField}
                  options={SIGN_UP_METHOD_OPTIONS}
                  disabled={submitting}
                  sx={radioFieldStyles}
                />
              </Box>

              <Box sx={boxStyles} rowGap={2}>
                <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                  <Button
                    type="submit"
                    sx={submitButtonStyles}
                    disabled={submitting || !valid}
                    loading={submitting}
                    size="large"
                    variant="contained"
                  >
                    Next step
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        }}
      </Form>
    </Box>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
};

const radioFieldStyles: SxProp = {
  mt: 2,
  [`& .${radioClasses.checked}`]: {
    borderColor: 'red',
  },
  [`& .${radioClasses.root}`]: {
    mb: 1,
    fontSize: '1rem',
  },
};

const boxStyles: SxProp = {
  display: 'flex',
  flexDirection: 'column',
};
